import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Award, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Card } from '../ds/Card';
import { Button } from '../ds/Button';

/**
 * ROICalculator - Compare cost vs performance for different upgrade paths
 *
 * Compares three scenarios:
 * 1. Adjust current club (loft/settings) - Free/$0
 * 2. Switch golf ball - $20-$60
 * 3. Buy new gear (head + shaft) - $200-$800 minus trade-in
 *
 * Shows yardage gained, cost, and cost per yard for each option
 */
export const ROICalculator = ({
  currentClub,           // { clubModel, shaftModel, loftDegrees, estimatedValue }
  currentPerformance,    // { carry_distance, total_distance, ball_speed, spin_rate }
  optimizationData,      // { recommended_loft, expected_gain_yards, metrics }
  ballRecommendation,    // { current_ball, recommended_ball, expected_gain }
  matrixRecommendation,  // { recommended_combo, expected_carry, expected_total, price }
  onClose
}) => {
  // Calculate scenarios first to determine best option
  const scenarios = [];

  // Scenario A: Adjust Current Club
  if (optimizationData && optimizationData.expected_gain_yards > 1) {
    scenarios.push({
      id: 'adjust',
      title: 'Adjust Your Current Club',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'blue',
      cost: 0,
      yardageGain: parseFloat(optimizationData.expected_gain_yards || 0),
      description: `Set your ${currentClub?.clubModel || 'club'} to ${optimizationData.recommended_loft}°`,
      details: [
        'Free - just adjust your loft sleeve',
        `Projected gain: +${Math.round(optimizationData.expected_gain_yards)} yards carry`,
        'Takes 30 seconds at the range',
        'Reversible anytime'
      ],
      costPerYard: 0,
      roi: Infinity // Free gains = infinite ROI
    });
  }

  // Scenario B: Switch Golf Ball
  if (ballRecommendation && ballRecommendation.expected_gain_yards > 2) {
    const ballCost = ballRecommendation.recommended_ball?.price || 45; // Default $45/dozen
    const yardageGain = parseFloat(ballRecommendation.expected_gain_yards || 0);

    scenarios.push({
      id: 'ball',
      title: 'Switch Golf Ball',
      icon: <Award className="w-5 h-5" />,
      color: 'emerald',
      cost: ballCost,
      yardageGain: yardageGain,
      description: `Switch to ${ballRecommendation.recommended_ball?.brand} ${ballRecommendation.recommended_ball?.model}`,
      details: [
        `Cost: $${Math.round(ballCost)}/dozen`,
        `Projected gain: +${Math.round(yardageGain)} yards`,
        'Better spin characteristics for your swing',
        'Immediate improvement'
      ],
      costPerYard: yardageGain > 0 ? (ballCost / yardageGain).toFixed(2) : 0,
      roi: yardageGain / ballCost
    });
  }

  // Scenario C: Buy New Gear
  if (matrixRecommendation) {
    const gearCost = matrixRecommendation.price || 550; // Default price
    const tradeInValue = currentClub?.estimatedValue || 0;
    const netCost = gearCost - tradeInValue;
    const yardageGain = (matrixRecommendation.expected_carry || 0) - (currentPerformance?.carry_distance || 0);

    scenarios.push({
      id: 'gear',
      title: 'Buy New Equipment',
      icon: <DollarSign className="w-5 h-5" />,
      color: 'purple',
      cost: netCost,
      yardageGain: yardageGain,
      description: matrixRecommendation.combo_name || 'New head + shaft combo',
      details: [
        `Equipment cost: $${Math.round(gearCost)}`,
        tradeInValue > 0 ? `Trade-in credit: +$${Math.round(tradeInValue)}` : 'No trade-in entered',
        `Net cost: $${Math.round(netCost)}`,
        `Projected gain: +${Math.round(yardageGain)} yards`
      ],
      costPerYard: yardageGain > 0 ? (netCost / yardageGain).toFixed(2) : 0,
      roi: yardageGain / netCost
    });
  }

  // Sort by ROI (best value first)
  scenarios.sort((a, b) => {
    if (a.roi === Infinity) return -1;
    if (b.roi === Infinity) return 1;
    return b.roi - a.roi;
  });

  const bestOption = scenarios[0];

  // Auto-select best option and keep detail pane open
  const [selectedScenario, setSelectedScenario] = useState(bestOption?.id || null);

  const colorClasses = {
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    purple: 'bg-purple-500/10 border-purple-500/30 text-purple-400'
  };

  if (scenarios.length === 0) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 text-zinc-400">
          <AlertCircle className="w-5 h-5" />
          <p>No upgrade options available at this time.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-white mb-1">ROI Analysis</h3>
          <p className="text-sm text-zinc-400">Compare cost vs. performance gains</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white transition-colors text-sm"
          >
            Close
          </button>
        )}
      </div>

      {/* Best Value Callout */}
      {bestOption && (
        <Card className={`p-4 border ${colorClasses[bestOption.color]}`}>
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold mb-1">Best Value: {bestOption.title}</div>
              <div className="text-sm opacity-90">
                {bestOption.cost === 0
                  ? `+${Math.round(bestOption.yardageGain)} yards for FREE`
                  : `+${Math.round(bestOption.yardageGain)} yards for $${Math.round(bestOption.cost)} ($${bestOption.costPerYard}/yard)`
                }
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Scenario Comparison Grid */}
      <div className="grid md:grid-cols-3 gap-4">
        {scenarios.map((scenario) => {
          const isSelected = selectedScenario === scenario.id;
          const selectedStyles = {
            blue: 'border-2 border-blue-500 ring-2 ring-blue-500/30 shadow-lg',
            emerald: 'border-2 border-emerald-500 ring-2 ring-emerald-500/30 shadow-lg',
            purple: 'border-2 border-purple-500 ring-2 ring-purple-500/30 shadow-lg'
          };

          return (
            <Card
              key={scenario.id}
              className={`p-4 cursor-pointer transition-all ${
                isSelected
                  ? selectedStyles[scenario.color]
                  : 'border border-border hover:border-zinc-600'
              }`}
              onClick={() => setSelectedScenario(scenario.id)}
            >

            {/* Icon & Title */}
            <div className="flex items-center gap-2 mb-3">
              <div className={`p-2 rounded ${colorClasses[scenario.color]}`}>
                {scenario.icon}
              </div>
              <div className="font-semibold text-white text-sm">{scenario.title}</div>
            </div>

            {/* Description */}
            <div className="text-xs text-zinc-400 mb-3">{scenario.description}</div>

            {/* Metrics */}
            <div className="space-y-2 mb-3">
              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Cost:</span>
                <span className="font-semibold text-white">
                  {scenario.cost === 0 ? 'FREE' : `$${Math.round(scenario.cost)}`}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Yardage Gain:</span>
                <span className="font-semibold text-emerald-400">
                  +{Math.round(scenario.yardageGain)} yds
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Cost/Yard:</span>
                <span className="font-semibold text-white">
                  {scenario.cost === 0 ? 'FREE' : `$${scenario.costPerYard}`}
                </span>
              </div>
            </div>

            {/* Best Value Badge */}
            {scenario.id === bestOption.id && (
              <div className="text-xs font-semibold text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Best Value
              </div>
            )}
          </Card>
        );
        })}
      </div>

      {/* Detailed Breakdown (when scenario selected) */}
      {selectedScenario && (
        <Card className="p-4 bg-zinc-900/50">
          <h4 className="font-semibold text-white mb-3">
            {scenarios.find(s => s.id === selectedScenario)?.title} Details
          </h4>
          <ul className="space-y-2">
            {scenarios.find(s => s.id === selectedScenario)?.details.map((detail, idx) => (
              <li key={idx} className="text-sm text-zinc-300 flex items-start gap-2">
                <span className="text-emerald-500 mt-0.5">•</span>
                {detail}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Summary */}
      <div className="text-xs text-zinc-500 text-center pt-2 border-t border-zinc-800">
        Trade-in values are estimates. Actual resale value may vary.
      </div>
    </div>
  );
};
